import React, { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import ScaleToFit from "./ScaleToFit";
import SideStarField from "./SideStarField";
import { useIsPortrait } from "./useIsPortrait";
import { useMode } from "./modeBus";
import HTLDSlide9Mobile from "./HTLDSlide9Mobile";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";
import { pick } from "i18n/lang";
import { localAudio } from "i18n/audio";
import { slide9Content, useSlide9, mudraSrc9, Seg } from "./slide9.data";

const accent = "#3ecb8a";
const font   = FONT_BODY;

if (typeof document !== "undefined" && !document.getElementById("yogyism-pulse-3ecb8a")) {
 const s = document.createElement("style");
 s.id = "yogyism-pulse-3ecb8a";
 s.textContent = `
   @keyframes yogyismPulse_3ecb8a {
     0%   { box-shadow: 0 0 0 0 rgba(62,203,138,0.70), 0 0 12px 2px rgba(62,203,138,0.45); transform: scale(1); }
     50%  { box-shadow: 0 0 0 16px rgba(62,203,138,0), 0 0 20px 6px rgba(62,203,138,0.65); transform: scale(1.12); }
     100% { box-shadow: 0 0 0 0 rgba(62,203,138,0), 0 0 12px 2px rgba(62,203,138,0.45); transform: scale(1); }
   }`;
 document.head.appendChild(s);
}

const S: Record<string, CSSProperties> = {
  root: {
   background: "#040f18", fontFamily: font, color: "#d0e8f8",
   width: "100%", height: "100%",
   display: "flex", flexDirection: "column",
   boxSizing: "border-box", overflow: "hidden",
   position: "relative",
  },
// Shared header row: breadcrumb (left) + slide title (right), above the divider line
subtitleBar: {
 background: "#050f1a", borderBottom: "1px solid rgba(62,203,138,0.12)",
 padding: "clamp(5px,0.6vw,9px) clamp(16px,3vw,40px)",
 display: "flex", alignItems: "center", justifyContent: "space-between",
 gap: "clamp(12px,2vw,28px)", flexShrink: 0,
},
body: {
 flex: 1, minHeight: 0,
 display: "flex", gap: "clamp(16px,2vw,28px)",
 padding: "clamp(10px,1.4vw,18px) clamp(16px,3vw,40px) clamp(10px,1.2vw,14px)",
 alignItems: "stretch", overflow: "hidden",
},
content: {
 flex: 1, minWidth: "min(100%, 380px)",
 display: "flex", flexDirection: "column",
 justifyContent: "space-between",
},
panel: {
 width: "clamp(220px,28vw,380px)", flexShrink: 0,
 display: "flex", flexDirection: "column", alignItems: "center",
 overflow: "hidden",
},
};

// ─── Nostril Guide Canvas Inner ───────────────────────────────────────────────
const NostrilGuideInner: React.FC<{ playing: boolean; onComplete?: () => void; }> = ({ playing, onComplete }) => {
  const canvasRef    = useRef<HTMLCanvasElement>(null);
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);
  
  const st = useRef({
   animId:         0,
   playing:        false,
   round:          0,
   cycleSecs:      24,
   introTimeoutId: 0,
   practiceAudio:  null as HTMLAudioElement | null,
   introAudio:     null as HTMLAudioElement | null,
   drawAnim:       null as (() => void) | null,
   drawStatic:     null as (() => void) | null,
   stopAudio:      null as (() => void) | null,
   startWithIntro: null as (() => void) | null,
  });
  
  useEffect(() => {
   const canvas = canvasRef.current; if (!canvas) return;
   const ctx = canvas.getContext("2d") as CanvasRenderingContext2D; if (!ctx) return;
   const W = 400, H = 560; canvas.width = W; canvas.height = H;
  
   const cx = 200, cy = 258;
   const R_MIN = 42, R_MAX = 90;
   const TOTAL_ROUNDS = 5;
   const c = pick(slide9Content).canvas; // slides remount on language change
  
   const phases = [
     { label: "INHALE", nostril: "LEFT",  action: "open", counts: 4 },
     { label: "HOLD",   nostril: "BOTH",  action: "hold", counts: 2 },
     { label: "EXHALE", nostril: "RIGHT", action: "open", counts: 6 },
     { label: "INHALE", nostril: "RIGHT", action: "open", counts: 4 },
     { label: "HOLD",   nostril: "BOTH",  action: "hold", counts: 2 },
     { label: "EXHALE", nostril: "LEFT",  action: "open", counts: 6 },
   ];
   const TOTAL_COUNTS = phases.reduce((a, p) => a + p.counts, 0); // 24
   const cumCounts = phases.reduce<number[]>((acc, p, i) => [...acc, acc[i] + p.counts], [0]); // [0,4,6,12,16,18,24]
  
   const stars = [[20,18,0.9],[72,10,0.7],[310,16,1.0],[370,40,0.7],[35,65,0.8],[350,60,0.9],[385,8,0.9],[110,42,0.7]];
   function easeInOut(t: number) { return t < 0.5 ? 2*t*t : -1+(4-2*t)*t; }
  
   // ── Audio: intro + single pre-recorded full cycle (both nostril sequences) ──
   // nadishuddhi-practice.mp3 is ONE full cycle (24 counts). Looped TOTAL_ROUNDS
   // times; visuals are driven from its currentTime so they never drift.
   const introAudio    = localAudio("/audio/intro-fingers.mp3");        introAudio.preload = "auto";
   const practiceAudio = localAudio("/audio/nadishuddhi-practice.mp3"); practiceAudio.preload = "auto";
   // Counting pace: the recording counts ~1 per 0.9 s; play it slower (pitch is
   // preserved). Visuals follow currentTime, so they slow down with it.
   practiceAudio.defaultPlaybackRate = practiceAudio.playbackRate = 0.8;
   st.current.introAudio    = introAudio;
   st.current.practiceAudio = practiceAudio;
   practiceAudio.addEventListener("loadedmetadata", () => {
     if (isFinite(practiceAudio.duration) && practiceAudio.duration > 0)
       st.current.cycleSecs = practiceAudio.duration;
   });
  
   function stopAllAudio() {
     introAudio.onended = null;
     try { introAudio.pause(); introAudio.currentTime = 0; } catch (_) {}
     practiceAudio.onended = null;
     try { practiceAudio.pause(); practiceAudio.currentTime = 0; } catch (_) {}
   }
  
   // Map a time position (seconds) to the current phase via count-proportional boundaries.
   function getPhaseAt(tSec: number, cyc: number) {
     const t = ((tSec % cyc) + cyc) % cyc;
     const tCounts = (t / cyc) * TOTAL_COUNTS;   // position in "count units"
     for (let i = 0; i < phases.length; i++) {
       if (tCounts < cumCounts[i + 1]) {
         const within   = tCounts - cumCounts[i];             // counts elapsed in this phase
         const progress = within / phases[i].counts;
         const count    = Math.max(1, Math.ceil(phases[i].counts - within));
         return { phase: phases[i], phaseIdx: i, count, progress };
       }
     }
     return { phase: phases[0], phaseIdx: 0, count: phases[0].counts, progress: 0 };
   }
  
   function drawNostril(x: number, y: number, label: string, isOpen: boolean) {
     const r = 34;
     if (isOpen) {
       const grd = ctx.createRadialGradient(x, y, 0, x, y, r + 18);
       grd.addColorStop(0, "rgba(62,203,138,0.30)"); grd.addColorStop(1, "rgba(62,203,138,0)");
       ctx.beginPath(); ctx.arc(x, y, r + 18, 0, Math.PI*2); ctx.fillStyle = grd; ctx.fill();
     }
     ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI*2);
     ctx.fillStyle   = isOpen ? "rgba(62,203,138,0.20)" : "rgba(62,203,138,0.03)"; ctx.fill();
     ctx.strokeStyle = isOpen ? "rgba(62,203,138,0.85)" : "rgba(62,203,138,0.15)";
     ctx.lineWidth   = isOpen ? 2.2 : 1.0; ctx.stroke();
     if (!isOpen) {
       ctx.strokeStyle = "rgba(62,203,138,0.35)"; ctx.lineWidth = 2;
       ctx.beginPath(); ctx.moveTo(x-9,y-9); ctx.lineTo(x+9,y+9);
       ctx.moveTo(x+9,y-9); ctx.lineTo(x-9,y+9); ctx.stroke();
     } else {
       ctx.beginPath(); ctx.arc(x, y, 6, 0, Math.PI*2); ctx.fillStyle = accent; ctx.fill();
     }
     ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
     ctx.font = "bold 12px 'JetBrains Mono','Courier New',monospace";
     ctx.fillStyle = isOpen ? accent : "rgba(62,203,138,0.28)";
     ctx.fillText(label, x, y + r + 16);
     ctx.font = "10px 'JetBrains Mono','Courier New',monospace";
     ctx.fillStyle = isOpen ? "rgba(62,203,138,0.65)" : "rgba(62,203,138,0.20)";
     ctx.fillText(isOpen ? c.open : c.closed, x, y + r + 29);
   }
  
   function drawBg() {
     ctx.clearRect(0, 0, W, H);
     ctx.fillStyle = "#040f18"; ctx.fillRect(0, 0, W, H);
     stars.forEach(([x, y, r]) => { ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI*2); ctx.fillStyle = "rgba(255,255,255,0.50)"; ctx.fill(); });
     ctx.beginPath(); ctx.arc(370,34,16,0,Math.PI*2); ctx.fillStyle="rgba(200,255,230,0.07)"; ctx.fill();
     ctx.beginPath(); ctx.arc(370,34,10,0,Math.PI*2); ctx.fillStyle="rgba(210,255,235,0.13)"; ctx.fill();
     ctx.beginPath(); ctx.arc(370,34, 7,0,Math.PI*2); ctx.fillStyle="#f0fff8"; ctx.fill();
   }
  
   function drawScene(tSec: number, cyc: number, cosmetic: number, roundLabel?: string) {
     const { phase, phaseIdx, count, progress } = getPhaseAt(tSec, cyc);
     let radius = R_MIN;
     if (phase.label === "INHALE")      radius = R_MIN + (R_MAX-R_MIN)*easeInOut(progress);
     else if (phase.label === "HOLD")   radius = R_MAX;
     else                               radius = R_MAX - (R_MAX-R_MIN)*easeInOut(progress);
  
     const circCY = cy - 50;
     const breathFill = (radius - R_MIN) / (R_MAX - R_MIN);
     const haloR = radius + 70;
     const halo = ctx.createRadialGradient(cx, circCY, radius * 0.4, cx, circCY, haloR);
     halo.addColorStop(0,   `rgba(62,203,138,${(0.18 + 0.22 * breathFill).toFixed(3)})`);
     halo.addColorStop(0.6, `rgba(62,203,138,${(0.06 + 0.08 * breathFill).toFixed(3)})`);
     halo.addColorStop(1,   "rgba(62,203,138,0)");
     ctx.beginPath(); ctx.arc(cx, circCY, haloR, 0, Math.PI*2); ctx.fillStyle = halo; ctx.fill();
     for (let i = 0; i < 5; i++) {
       const rp = ((cosmetic * 0.5 + i * 20) % 100) / 100;
       const rr = radius + 4 + rp * 70;
       const ra = (1 - rp) * (0.14 + 0.18 * breathFill);
       ctx.beginPath(); ctx.arc(cx, circCY, rr, 0, Math.PI*2);
       ctx.strokeStyle = `rgba(62,203,138,${ra.toFixed(3)})`; ctx.lineWidth = 1.2; ctx.stroke();
     }
     const grd = ctx.createRadialGradient(cx - radius*0.3, circCY - radius*0.3, 2, cx, circCY, radius);
     grd.addColorStop(0,    `rgba(180,255,220,${(0.45 + 0.25 * breathFill).toFixed(3)})`);
     grd.addColorStop(0.45, `rgba(62,203,138,${(0.28 + 0.12 * breathFill).toFixed(3)})`);
     grd.addColorStop(1,    "rgba(62,203,138,0.05)");
     ctx.beginPath(); ctx.arc(cx,circCY,radius,0,Math.PI*2); ctx.fillStyle=grd; ctx.fill();
     const sa = (cosmetic * 0.02) % (Math.PI*2);
     ctx.beginPath(); ctx.arc(cx, circCY, radius*0.82, sa, sa + Math.PI*0.6);
     ctx.strokeStyle = `rgba(180,255,220,${(0.18 + 0.18 * breathFill).toFixed(3)})`; ctx.lineWidth = 2; ctx.stroke();
     ctx.strokeStyle = `rgba(62,203,138,${(0.70 + 0.22 * breathFill).toFixed(3)})`;
     ctx.lineWidth = 2.2; ctx.stroke();
     ctx.textAlign="center";
     ctx.font="bold 17px 'JetBrains Mono','Courier New',monospace"; ctx.fillStyle=accent; ctx.fillText(c.phase[phase.label],cx,circCY-12,2*R_MIN-6);
     ctx.font="bold 44px 'JetBrains Mono','Courier New',monospace"; ctx.fillStyle="rgba(62,203,138,0.90)";
     ctx.textBaseline="middle"; ctx.fillText(String(count),cx,circCY+10); ctx.textBaseline="alphabetic";
  
     const nY = cy + 80;
     const leftOpen  = phase.nostril === "LEFT"  && phase.action === "open";
     const rightOpen = phase.nostril === "RIGHT" && phase.action === "open";
     ctx.beginPath(); ctx.moveTo(cx-20,nY-20); ctx.quadraticCurveTo(cx,nY-50,cx+20,nY-20);
     ctx.strokeStyle="rgba(62,203,138,0.18)"; ctx.lineWidth=1.5; ctx.stroke();
     drawNostril(cx-55, nY, c.left,  leftOpen);
     drawNostril(cx+55, nY, c.right, rightOpen);
     ctx.textAlign="center"; ctx.textBaseline="alphabetic";
     ctx.font="13px 'JetBrains Mono','Courier New',monospace"; ctx.fillStyle="rgba(62,203,138,0.55)";
     ctx.fillText(phase.action==="hold"?c.bothClosed:c.nostril[phase.nostril], cx, nY+82);
  
     const arcY = nY+125;
     ctx.beginPath(); ctx.arc(cx,arcY,26,-Math.PI/2,-Math.PI/2+Math.PI*2);
     ctx.strokeStyle="rgba(62,203,138,0.08)"; ctx.lineWidth=2.5; ctx.stroke();
     ctx.beginPath(); ctx.arc(cx,arcY,26,-Math.PI/2,-Math.PI/2+progress*Math.PI*2);
     ctx.strokeStyle=accent; ctx.lineWidth=2.5; ctx.stroke();
     ctx.font="bold 32px 'JetBrains Mono','Courier New',monospace"; ctx.fillStyle="rgba(62,203,138,0.88)";
     ctx.textBaseline="middle"; ctx.fillText(String(count),cx,arcY); ctx.textBaseline="alphabetic";
  
     const barY=arcY+40, barX=30, barW=W-60;
     phases.forEach((p,i) => {
       const sw=(p.counts/TOTAL_COUNTS)*barW;
       const sx=barX+(cumCounts[i]/TOTAL_COUNTS)*barW;
       const cur=i===phaseIdx;
       ctx.fillStyle=cur?"rgba(62,203,138,0.28)":"rgba(62,203,138,0.07)"; ctx.fillRect(sx,barY,sw-2,18);
       ctx.strokeStyle=cur?"rgba(62,203,138,0.55)":"rgba(62,203,138,0.12)"; ctx.lineWidth=1; ctx.strokeRect(sx,barY,sw-2,18);
       ctx.font="9px 'JetBrains Mono','Courier New',monospace"; ctx.fillStyle=cur?accent:"rgba(62,203,138,0.28)";
       ctx.textAlign="center"; ctx.fillText(`${c.abbr[p.label]}${p.counts}`,sx+(sw-2)/2,barY+12);
     });
  
     ctx.font="13px 'Space Grotesk',Arial,sans-serif"; ctx.fillStyle="rgba(62,203,138,0.32)";
     ctx.textAlign="center";
     ctx.fillText(roundLabel ?? c.caption, cx, barY+30);
   }
  
   function drawStatic() {
     drawBg(); drawScene(0, st.current.cycleSecs, 0);
     ctx.fillStyle="rgba(4,15,24,0.40)"; ctx.fillRect(0,0,W,H);
     ctx.textAlign="center"; ctx.font="14px 'Space Grotesk',Arial,sans-serif";
     ctx.fillStyle="rgba(62,203,138,0.38)"; ctx.fillText(c.pressToBegin,cx,H-8);
   }
  
   function drawAnim() {
     if (!st.current.playing) return;
     drawBg();
     const a    = st.current.practiceAudio;
     const cyc  = st.current.cycleSecs || 24;
     const tSec = a ? a.currentTime : 0;
     const currentRound = Math.min(st.current.round + 1, TOTAL_ROUNDS);
     drawScene(tSec, cyc, performance.now() / 16, c.round(currentRound, TOTAL_ROUNDS));
     st.current.animId = requestAnimationFrame(drawAnim);
   }
  
   function beginPractice() {
     clearTimeout(st.current.introTimeoutId);
     if (st.current.playing) return;
     const a = st.current.practiceAudio;
     if (!a) return;
     st.current.playing = true;
     st.current.round   = 0;
     a.currentTime = 0;
     a.onended = () => {
       st.current.round++;
       if (st.current.round >= TOTAL_ROUNDS) {
         st.current.playing = false;
         stopAllAudio();
         drawStatic();
         onCompleteRef.current?.();
       } else {
         a.currentTime = 0;
         a.play().catch(() => {});
       }
     };
     a.play().catch(() => {});
     st.current.drawAnim?.();
   }
  
   function startWithIntro() {
     const intro = st.current.introAudio;
     if (!intro) { beginPractice(); return; }
     intro.onended = () => {
       intro.onended = null;
       st.current.introTimeoutId = window.setTimeout(beginPractice, 1000);
     };
     intro.currentTime = 0;
     intro.play().then(() => {
       const dur = intro.duration;
       const ms  = isFinite(dur) ? Math.ceil(dur * 1000) + 2000 : 30000;
       st.current.introTimeoutId = window.setTimeout(beginPractice, ms);
     }).catch(beginPractice);
   }
  
   st.current.drawAnim       = drawAnim;
   st.current.drawStatic     = drawStatic;
   st.current.stopAudio      = stopAllAudio;
   st.current.startWithIntro = startWithIntro;
   drawStatic();
  
   const cur = st.current;
   return () => {
     cancelAnimationFrame(cur.animId);
     clearTimeout(cur.introTimeoutId);
     stopAllAudio();
   };
  }, []); // eslint-disable-line
  
  useEffect(() => {
   const s = st.current;
   cancelAnimationFrame(s.animId);
   clearTimeout(s.introTimeoutId);
   s.playing = false;
   s.stopAudio?.();
   if (playing) { s.startWithIntro?.(); } else { s.drawStatic?.(); }
  }, [playing]);
  
  return (
   <canvas ref={canvasRef} style={{ display: "block", borderRadius: 8, width: "100%", height: "100%", objectFit: "contain" }} />
  );
  };

// ─── Nostril Guide Panel ──────────────────────────────────────────────────────
const NostrilGuide: React.FC = () => {
const [playing, setPlaying] = useState(false);
const btnAccent = "#3ecb8a";

const audioCtx   = useRef<AudioContext | null>(null);
const masterGain = useRef<GainNode | null>(null);
const oscs       = useRef<OscillatorNode[]>([]);
const lfo        = useRef<OscillatorNode | null>(null);
const binOscs    = useRef<OscillatorNode[]>([]);
const binGain    = useRef<GainNode | null>(null);

const startAudio=()=>{const ctx=new(window.AudioContext||(window as any).webkitAudioContext)();audioCtx.current=ctx;const gain=ctx.createGain();gain.gain.setValueAtTime(0,ctx.currentTime);gain.gain.linearRampToValueAtTime(0.04,ctx.currentTime+2.0);gain.connect(ctx.destination);masterGain.current=gain;const delay=ctx.createDelay(1.0);const fbG=ctx.createGain();delay.delayTime.value=0.45;fbG.gain.value=0.35;delay.connect(fbG);fbG.connect(delay);delay.connect(gain);oscs.current=([[138.6,0.55],[207.65,0.28],[277.2,0.14]] as[number,number][]).map(([freq,vol])=>{const osc=ctx.createOscillator();const og=ctx.createGain();osc.type="sine";osc.frequency.value=freq;og.gain.value=vol;osc.connect(og);og.connect(delay);og.connect(gain);osc.start();return osc;});const lfoNode=ctx.createOscillator();const lfoG=ctx.createGain();lfoNode.frequency.value=0.083;lfoG.gain.value=0;lfoNode.connect(lfoG);lfoG.connect(gain.gain);lfoNode.start();lfo.current=lfoNode;const merger=ctx.createChannelMerger(2);const bGain=ctx.createGain();bGain.gain.setValueAtTime(0,ctx.currentTime);bGain.gain.linearRampToValueAtTime(0,ctx.currentTime+3.5);merger.connect(bGain);bGain.connect(ctx.destination);binGain.current=bGain;const lOsc=ctx.createOscillator();const lG=ctx.createGain();lOsc.type="sine";lOsc.frequency.value=200;lG.gain.value=1;lOsc.connect(lG);lG.connect(merger,0,0);lOsc.start();const rOsc=ctx.createOscillator();const rG=ctx.createGain();rOsc.type="sine";rOsc.frequency.value=206;rG.gain.value=1;rOsc.connect(rG);rG.connect(merger,0,1);rOsc.start();binOscs.current=[lOsc,rOsc];};


const stopAudio = () => {
 if (!audioCtx.current) return;
 const ctx = audioCtx.current;
 masterGain.current?.gain.linearRampToValueAtTime(0, ctx.currentTime + 1.2);
 binGain.current?.gain.linearRampToValueAtTime(0, ctx.currentTime + 1.2);
 setTimeout(() => {
   [...oscs.current, ...binOscs.current].forEach(o => { try { o.stop(); } catch (_) {} });
   try { lfo.current?.stop(); } catch (_) {}
   ctx.close().catch(() => {});
   audioCtx.current = masterGain.current = lfo.current = binGain.current = null;
   oscs.current = binOscs.current = [];
 }, 1300);
};

useEffect(() => {
 return () => {
   [...oscs.current, ...binOscs.current].forEach(o => { try { o.stop(); } catch (_) {} });
   try { lfo.current?.stop(); } catch (_) {}
   audioCtx.current?.close().catch(() => {});
 };
}, []);

useEffect(() => {
   const stop = () => { setPlaying(false); stopAudio(); };
   window.addEventListener('yogyism:stop', stop);
   return () => window.removeEventListener('yogyism:stop', stop);
   }, []); // eslint-disable-line

const handleToggle = () => {
   const next = !playing;
   setPlaying(next);
   if (next) {
       startAudio();
       window.dispatchEvent(new Event('yogyism:play')); // ← pause slideshow
   } else {
       stopAudio();
       
   }
};

return (
 <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", height: "100%" }}>
   <div style={{ flexShrink: 0, marginBottom: "clamp(4px,0.5vw,6px)" }}>
     <button
       onClick={handleToggle}
       style={{
         width: 32, height: 32,
         display: "flex", alignItems: "center", justifyContent: "center",
         background: playing ? "rgba(62,203,138,0.08)" : "rgba(62,203,138,0.32)",
         border: `1px solid rgba(62,203,138,${playing ? 0.32 : 0.85})`,
         borderRadius: "50%", cursor: "pointer",
         color: playing ? "rgba(62,203,138,0.60)" : btnAccent,
         transition: "all 0.2s",
         animation: playing ? "none" : "yogyismPulse_3ecb8a 1.3s ease-in-out infinite",
       }}
       onMouseEnter={e => (e.currentTarget.style.background = "rgba(62,203,138,0.28)")}
       onMouseLeave={e => (e.currentTarget.style.background = playing ? "rgba(62,203,138,0.08)" : "rgba(62,203,138,0.20)")}
     >
       {playing ? (
         <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
           <rect x="0" y="0" width="10" height="10" rx="1"/>
         </svg>
       ) : (
         <svg width="9" height="10" viewBox="0 0 9 10" fill="currentColor">
           <polygon points="0,0 9,5 0,10"/>
         </svg>
       )}
     </button>
   </div>
   <div style={{ flex: 1, minHeight: 0, width: "100%", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
     <NostrilGuideInner
       playing={playing}
       onComplete={() => {
        setPlaying(false);
        stopAudio();
        // Natural finish (not user-stop) → advance the deck to the next slide
        window.dispatchEvent(new Event("yogyism:next"));
      }}
     />
   </div>
 </div>
);
};

// ─── Slide content (authored at the ScaleToFit design size) ────────────────────
const renderSegs = (segs: Seg[], boldColor: string) =>
  segs.map((s, i) =>
    s.b ? <span key={i} style={{ color: boldColor, fontWeight: 700 }}>{s.t}</span>
    : s.em ? <em key={i}>{s.t}</em>
    : <React.Fragment key={i}>{s.t}</React.Fragment>
  );

const HTLDSlide9Inner: React.FC = () => {
const { meta, steps, what, duration } = useSlide9();
const fontLinkRef = useRef<HTMLLinkElement | null>(null);

useEffect(() => {
 const existing = document.querySelector('link[href*="Space+Grotesk"]') as HTMLLinkElement | null;
 if (!existing) {
   const link = document.createElement("link");
   link.rel  = "stylesheet";
   link.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
   document.head.appendChild(link);
   fontLinkRef.current = link;
 }
 return () => {
   if (fontLinkRef.current && document.head.contains(fontLinkRef.current)) {
     document.head.removeChild(fontLinkRef.current); fontLinkRef.current = null;
   }
 };
}, []);

const lbl: CSSProperties = {
 fontSize: "clamp(8px,0.7vw,11px)", fontFamily: FONT_MONO,
 color: accent, letterSpacing: 3, textTransform: "uppercase",
 marginBottom: "clamp(4px,0.55vw,8px)", fontStyle: "normal",
};
const txt: CSSProperties = {
 fontSize: "clamp(12px,1.0vw,15px)", fontStyle: "normal", fontFamily: FONT_BODY,
 lineHeight: 1.7, color: "#cde0f2", margin: 0,
};

const rootRef = useRef<HTMLDivElement>(null);

return (
<div style={S.root} ref={rootRef}>      {/* add ref={rootRef} */}
<SideStarField rootRef={rootRef} />   {/* add this line */}
  {/* Vishnu mudra — fixed in the upper-center gap, always visible */}
<img
  src={mudraSrc9}
  alt={meta.mudraAlt}
  style={{
    position: "absolute",
    top: "62%",
    left: "52%",
    transform: "translate(-50%,-50%)",
    width: "clamp(120px,15vw,200px)",
    height: "auto",
    opacity: 0.92,
    zIndex: 5,
    pointerEvents: "none",
  }}
/>

   {/* Header row: breadcrumb (left) + slide title (right), above the horizontal line */}
   <div style={S.subtitleBar}>
     <div style={{
       fontSize: "clamp(11px,1.0vw,14px)", fontStyle: "normal", color: "#7aa8cc",
       fontFamily: FONT_BODY,
     }}>
       {meta.breadcrumbPre} &nbsp;·&nbsp;{" "}
       <span style={{ color: accent }}>{meta.breadcrumbAccent}</span>
     </div>
     <div style={{
       fontSize: "clamp(16px,1.9vw,28px)", fontStyle: "normal", fontWeight: 600,
       color: "#e8f4ff", fontFamily: FONT_DISPLAY, letterSpacing: 0.5, lineHeight: 1,
       whiteSpace: "nowrap",
     }}>
       {meta.headerTitlePre}<span style={{ color: accent }}>{meta.headerTitleAccent}</span>
     </div>
   </div>

   <div style={S.body}>
     <div style={S.content}>
       <div>
         <div style={{ fontSize: "clamp(8px,0.68vw,11px)", fontFamily: FONT_MONO, color: "rgba(62,203,138,0.52)", letterSpacing: 3, marginBottom: "clamp(4px,0.5vw,7px)" }}>
           {meta.sanskrit}
         </div>
         <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(19px,2.3vw,30px)", fontStyle: "normal", fontWeight: 600, color: "#e8f4ff", margin: "0 0 clamp(2px,0.35vw,6px)", lineHeight: 1.2 }}>
           {meta.titlePre}<span style={{ color: accent }}>{meta.titleAccentWord}</span>{meta.titlePost}
         </h1>
         <p style={{ fontFamily: FONT_BODY, fontSize: "clamp(12px,1.1vw,15px)", fontStyle: "normal", fontWeight: 300, color: "#7aa8cc", margin: 0, letterSpacing: 0.3 }}>
           {meta.subtitle}
         </p>
       </div>

       <div style={{ borderBottom: "1px solid rgba(62,203,138,0.10)", paddingBottom: "clamp(8px,1vw,14px)" }}>
         <div style={lbl}>{meta.whatLabel}</div>
         <p style={txt}>{renderSegs(what, "#a0f0cc")}</p>
       </div>

       <div style={{ borderBottom: "1px solid rgba(62,203,138,0.10)", paddingBottom: "clamp(8px,1vw,14px)" }}>
         <div style={lbl}>{meta.cycleLabel}</div>
         <div style={{ display: "flex", flexDirection: "column", gap: "clamp(6px,0.8vw,10px)" }}>
           {steps.map((item, i) => (
             <div key={i} style={{ display: "flex", gap: "clamp(8px,1vw,14px)", alignItems: "flex-start" }}>
               <div style={{ width: 7, height: 7, borderRadius: "50%", background: accent, flexShrink: 0, marginTop: 6, opacity: 0.7 }} />
               <div>
                 <span style={{ fontFamily: FONT_BODY, fontSize: "clamp(11px,0.92vw,14px)", fontStyle: "normal", fontWeight: 700, color: "#b8f0d8" }}>{item.step} &nbsp;</span>
                 <span style={{ fontFamily: FONT_BODY, fontSize: "clamp(11px,0.92vw,14px)", fontStyle: "normal", fontWeight: 300, color: "#9bbdd6" }}>{item.desc}</span>
               </div>
             </div>
           ))}
         </div>
       </div>

       <div style={{ background: "rgba(62,203,138,0.06)", border: "1px solid rgba(62,203,138,0.22)", borderRadius: 8, padding: "clamp(11px,1.3vw,18px) clamp(13px,1.5vw,20px)" }}>
         <div style={lbl}>{meta.durationLabel}</div>
         <p style={{ ...txt, color: "#a8f0d0" }}>{renderSegs(duration, "#c8ffe8")}</p>
       </div>
     </div>

     {/* Panel — nostril guide fills the full panel height */}
     <div style={S.panel}>
       <NostrilGuide />
     </div>
   </div>

 </div>
);
};

const HTLDSlide9GuideMobile: React.FC = () => {
  const { meta } = useSlide9();
  return (
  <div style={{ background:"#040f18", color:"#d0e8f8", fontFamily:font, height:"100%", width:"100%", boxSizing:"border-box", display:"flex", flexDirection:"column", padding:"18px 16px 20px", overflow:"hidden" }}>
    <div style={{ flexShrink:0, textAlign:"center", marginBottom:10 }}>
      <div style={{ fontFamily:FONT_MONO, fontSize:10, letterSpacing:2, color:"rgba(62,203,138,0.7)", textTransform:"uppercase", marginBottom:4 }}>{meta.breadcrumbPre}</div>
      <h1 style={{ fontFamily: FONT_DISPLAY, fontStyle:"normal", fontWeight:600, fontSize:22, margin:0, color:"#e8f4ff" }}>{meta.headerTitlePre}<span style={{color:accent}}>{meta.headerTitleAccent}</span></h1>
      <p style={{ fontFamily: FONT_BODY, fontStyle:"normal", fontWeight:300, fontSize:12.5, color:"#7aa8cc", margin:"3px 0 0" }}>{meta.guideSummary}</p>
    </div>
    <div style={{ flex:1, minHeight:0, width:"100%", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden" }}>
      <NostrilGuide />
    </div>
  </div>
  );
};

// Scale the whole slide as one unit so it looks identical across screen
// resolutions, window sizes, and browser zoom — no reflow, no clipping.
const HTLDSlide9: React.FC = () => {
  const portrait = useIsPortrait();
  const mode = useMode();
  if (!portrait) return <ScaleToFit><HTLDSlide9Inner /></ScaleToFit>;
  return mode === "practice" ? <HTLDSlide9GuideMobile /> : <HTLDSlide9Mobile />;
};

export default HTLDSlide9;